import React, { useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import {
  setTimer,
  setTimerDone,
  setStartImageProcessing,
} from "../../state/actions";
import "./timer.style.scss";
import { UNLIMITED_TIMER_MAX } from "../timeroptions/TimerOptions";

type Props = {
  setStartImageProcessing: (bool: boolean) => void;
  timer: number;
  splitRatio?: number;
  leftHand?: boolean;
};
const Timer = ({
  setStartImageProcessing,
  timer,
  splitRatio = 0.5,
  leftHand = false,
}: Props) => {
  const loader = useRef(null);
  const [showTitle, setShowTitle] = React.useState(true);
  const [fadeEffect, setFadeEffect] = React.useState("fade-in");

  const [showEndingHint, setShowEndingHint] = React.useState(false);
  useEffect(() => {
    setTimeout(() => {
      setFadeEffect("fade-out"); // Start fading out after 3 seconds
    }, 3000);

    setTimeout(() => {
      setShowTitle(false); // Remove the element after fade-out completes
    }, 4000);
  }, []);

  useEffect(() => {
    let processIntrupted = false;
    let alpha = 360;
    let pi = Math.PI;
    let t = timer * 2.7;
    let start = new Date().getTime();
    let done = false;
    const draw = () => {
      alpha--;
      if (alpha === 0 && !processIntrupted) {
        done = true;
        var end = (new Date() as any) - start;
        console.info("Execution time: %dms", end);
        setStartImageProcessing(true);
      }
      var r = (alpha * pi) / 180,
        x = Math.sin(r) * 125,
        y = Math.cos(r) * -125,
        mid = alpha > 180 ? 1 : 0,
        anim = "M 0 0 v -125 A 125 125 1 " + mid + " 1 " + x + " " + y + " z";

      loader?.current?.setAttribute("d", anim);
      if (!done && !processIntrupted) {
        setTimeout(draw, t); // Redraw
      }
      if (alpha < 45 && !showEndingHint) {
        setShowEndingHint(true);
      }
    };
    draw();
    return () => {
      processIntrupted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setStartImageProcessing, timer]);

  const positions = useMemo(() => {
    const screenWidth =
      document.documentElement.clientWidth ||
      document.body.clientWidth ||
      window.innerWidth;
    const screenHeight =
      document.documentElement.clientHeight ||
      document.body.clientHeight ||
      window.innerHeight;
    const isPortrait = screenWidth < screenHeight;
    const ratio = Math.max(0.25, Math.min(0.75, splitRatio));
    if (isPortrait) {
      const borderPx = Math.floor(screenHeight * ratio);
      return {
        isPortrait: true,
        svgTop: borderPx - 50, // svg is 100px tall
        titleTop: borderPx - 90,
        hintTop: borderPx - 105,
      } as any;
    } else {
      const borderPx = Math.floor(screenWidth * ratio);
      return {
        isPortrait: false,
        svgLeft: borderPx - 50, // svg is 100px wide
        titleLeft: borderPx - 70, // title width ~140px (center by ~70px)
        hintLeft: borderPx - 50, // hint width ~100px (center by ~50px)
      } as any;
    }
  }, [splitRatio]);

  return (
    <div
      className="timer"
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
    >
      {showTitle && (
        <div
          className={`timer__title ${fadeEffect}`}
          style={
            positions.isPortrait
              ? {
                  top: `${positions.titleTop}px`,
                  left: "50%",
                  right: "auto",
                  transform: "translateX(-50%)",
                }
              : { left: `${positions.titleLeft}px` }
          }
        >
          {timer === UNLIMITED_TIMER_MAX ? "Unlimited timer" : "Timer starts!"}
        </div>
      )}
      {showEndingHint && (
        <div
          className="timer__time-over-hint"
          style={
            positions.isPortrait
              ? {
                  top: `${positions.hintTop}px`,
                  left: "50%",
                  right: "auto",
                  transform: "translateX(-50%)",
                }
              : { left: `${positions.hintLeft}px` }
          }
        >
          Time is almost over!
        </div>
      )}
      {timer !== UNLIMITED_TIMER_MAX && (
        <svg
          className={`timer__timer ${
            showEndingHint ? "timer__timer--show-ending-hint" : ""
          }`}
          width="100"
          height="100"
          style={
            positions.isPortrait
              ? {
                  top: `${positions.svgTop}px`,
                  bottom: "auto",
                  left: "50%",
                  right: "auto",
                  margin: 0,
                  transform: "translateX(-50%)",
                }
              : { left: `${positions.svgLeft}px`, right: "auto" }
          }
        >
          <path
            id="loader"
            className={`path ${showEndingHint ? "path--show-ending-hint" : ""}`}
            ref={loader}
            transform="translate(50, 50) scale(.200)"
          />
        </svg>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const { timer, timerDone, splitRatio, leftHand } = state;
  return { timer, timerDone, splitRatio, leftHand };
};
const mapDispatchToProps = (dispatch) => {
  return {
    setTimer: (payload) => dispatch(setTimer(payload)),
    setTimerDone: (payload) => dispatch(setTimerDone(payload)),
    setStartImageProcessing: (payload) =>
      dispatch(setStartImageProcessing(payload)),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(Timer);
